import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronRight, Check, Lock, Star, Zap, Award, Briefcase,
  AlertCircle, ChevronLeft, Tag, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Tier accent/icon map (supplementary visuals, not from DB) ─────────────────
const TIER_META = {
  free:      { Icon: Briefcase, badgeClass: 'plan-badge-muted' },
  featured:  { Icon: Star,      badgeClass: 'plan-badge-teal'  },
  premium:   { Icon: Award,     badgeClass: 'plan-badge-gold'  },
  executive: { Icon: Zap,       badgeClass: 'plan-badge-navy'  },
};

// ── Plan Card ─────────────────────────────────────────────────────────────────
function PlanCard({ plan, selected, onSelect, promoUnlocksFree }) {
  const meta   = TIER_META[plan.key] || { Icon: Briefcase, badgeClass: 'plan-badge-muted' };
  const Icon   = meta.Icon;
  const locked = plan.is_gated && !promoUnlocksFree;
  const active = selected && !locked;

  return (
    <div
      className={`plan-card${active ? ' plan-card--selected' : ''}${locked ? ' plan-card--locked' : ''}`}
      style={{ '--plan-accent': plan.accent_color, '--plan-bg': plan.bg_color }}
      onClick={() => !locked && onSelect(plan.key)}
      data-testid={`plan-card-${plan.key}`}
      role="button"
      tabIndex={locked ? -1 : 0}
      onKeyDown={e => e.key === 'Enter' && !locked && onSelect(plan.key)}
      aria-pressed={active}
    >
      {/* Top row */}
      <div className="plan-card__head">
        <div className="plan-card__icon-wrap">
          <Icon size={20} />
        </div>
        <div className="plan-card__badges">
          {plan.is_popular && (
            <span className="plan-badge plan-badge-popular" data-testid={`plan-badge-popular-${plan.key}`}>
              Popular
            </span>
          )}
          {plan.badge_label && (
            <span className={`plan-badge ${meta.badgeClass}`} data-testid={`plan-badge-label-${plan.key}`}>
              {plan.badge_label}
            </span>
          )}
          {locked && (
            <span className="plan-badge plan-badge-locked">
              <Lock size={10} style={{ marginRight: 3 }} />
              Advertiser Only
            </span>
          )}
        </div>
      </div>

      {/* Price */}
      <div className="plan-card__price" data-testid={`plan-price-${plan.key}`}>
        <span className="plan-card__price-main">{plan.price_label}</span>
        {plan.price > 0 && <span className="plan-card__price-sub">one-time</span>}
      </div>

      {/* Label */}
      <h3 className="plan-card__label" data-testid={`plan-label-${plan.key}`}>
        {plan.label}
      </h3>

      {/* Stats row */}
      <div className="plan-card__stats">
        <div className="plan-stat">
          <span className="plan-stat__num">{plan.post_count}</span>
          <span className="plan-stat__lbl">{plan.post_count === 1 ? 'Listing' : 'Listings'}</span>
        </div>
        <div className="plan-stat__divider" />
        <div className="plan-stat">
          <span className="plan-stat__num">{plan.duration_days}</span>
          <span className="plan-stat__lbl">Days</span>
        </div>
      </div>

      {/* Perks */}
      <ul className="plan-card__perks">
        {plan.perks.map((perk, i) => (
          <li key={i} className="plan-card__perk">
            <Check size={13} className="plan-perk-check" />
            <span>{perk}</span>
          </li>
        ))}
      </ul>

      {/* Locked note */}
      {locked && plan.note && (
        <p className="plan-card__locked-note">
          <AlertCircle size={12} style={{ marginRight: 4, flexShrink: 0 }} />
          {plan.note}
        </p>
      )}

      {/* Select indicator */}
      {!locked && (
        <div className={`plan-card__cta${active ? ' plan-card__cta--selected' : ''}`}>
          {active ? (
            <><Check size={14} /> Selected</>
          ) : (
            <>Select {plan.label} <ChevronRight size={14} /></>
          )}
        </div>
      )}
    </div>
  );
}

// ── Coupon row ────────────────────────────────────────────────────────────────
function CouponRow({ coupon, onChange, onApply, onRemove, status, discount }) {
  return (
    <div className="ch-coupon-row" data-testid="coupon-row">
      <div className="ch-coupon-input-wrap">
        <Tag size={14} className="ch-coupon-icon" />
        <input
          type="text"
          placeholder="Promo or comp code"
          value={coupon}
          onChange={e => onChange(e.target.value.toUpperCase())}
          className="ch-coupon-input"
          data-testid="coupon-input"
        />
        {coupon && status === 'none' && (
          <button type="button" className="ch-coupon-apply-btn" onClick={onApply} data-testid="coupon-apply-btn">
            Apply
          </button>
        )}
        {status !== 'none' && (
          <button type="button" className="ch-coupon-remove-btn" onClick={onRemove} data-testid="coupon-remove-btn">
            <X size={13} />
          </button>
        )}
      </div>
      {status === 'applied' && (
        <p className="ch-coupon-msg ch-coupon-msg--ok" data-testid="coupon-applied-msg">
          <Check size={12} /> Code applied — {discount}% off
        </p>
      )}
      {status === 'free_unlocked' && (
        <p className="ch-coupon-msg ch-coupon-msg--ok" data-testid="coupon-free-msg">
          <Check size={12} /> Advertiser code accepted — Free tier unlocked
        </p>
      )}
      {status === 'invalid' && (
        <p className="ch-coupon-msg ch-coupon-msg--err" data-testid="coupon-invalid-msg">
          <AlertCircle size={12} /> Invalid code. Try again or contact support.
        </p>
      )}
    </div>
  );
}

// ── Main form ─────────────────────────────────────────────────────────────────
export default function PostJobPage() {
  const navigate = useNavigate();

  // Plans from API
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  // Form state
  const [selectedPlan, setSelectedPlan] = useState('featured');
  const [form, setForm] = useState({
    // Employer
    organization: '', logo_initial: '', contact_name: '', contact_email: '', contact_phone: '',
    // Listing
    title: '', location: '', listing_type: 'job', work_type: 'hybrid',
    experience_level: 'mid', category: 'technology', salary_range: '',
    description: '', description_short: '', skills: '', requirements: '',
    // Apply
    apply_method: 'external_url', apply_url: '', apply_email: '',
    // Executive extras
    company_logo_url: '', company_banner_url: '',
    // Payment
    payment_method: 'credit_card',
  });
  const [coupon, setCoupon]           = useState('');
  const [couponStatus, setCouponStatus] = useState('none');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [promoUnlocksFree, setPromoUnlocksFree] = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [submitted, setSubmitted]     = useState(false);
  const [submittedId, setSubmittedId] = useState(null);

  // Load plans
  useEffect(() => {
    axios.get(`${API}/job-plans`)
      .then(r => { setPlans(r.data); setPlansLoading(false); })
      .catch(() => { setPlansLoading(false); });
  }, []);

  const selectedPlanData = plans.find(p => p.key === selectedPlan) || null;

  // Price calculation
  const basePrice = selectedPlanData?.price ?? 0;
  const discountAmt = couponStatus === 'applied' ? Math.round(basePrice * couponDiscount / 100) : 0;
  const finalPrice = basePrice - discountAmt;

  const f = key => e => setForm(prev => ({ ...prev, [key]: e.target.value }));

  // Coupon logic
  const COUPONS = { 'OP15': 15, 'SAVE20': 20, 'OP25': 25 };
  const FREE_CODES = ['OPFREE', 'OPROADSHOW', 'ADVERTISER'];

  const applyCoupon = () => {
    const code = coupon.trim().toUpperCase();
    if (FREE_CODES.includes(code)) {
      setCouponStatus('free_unlocked');
      setPromoUnlocksFree(true);
      setSelectedPlan('free');
      return;
    }
    if (COUPONS[code]) {
      setCouponStatus('applied');
      setCouponDiscount(COUPONS[code]);
    } else {
      setCouponStatus('invalid');
    }
  };

  const removeCoupon = () => {
    setCoupon('');
    setCouponStatus('none');
    setCouponDiscount(0);
    setPromoUnlocksFree(false);
    if (selectedPlan === 'free') setSelectedPlan('featured');
  };

  // Derive is_featured + is_hotlist from plan
  const planFlags = {
    free:      { is_featured: false, is_hotlist: false },
    basic:     { is_featured: false, is_hotlist: false },
    featured:  { is_featured: true,  is_hotlist: false },
    premium:   { is_featured: true,  is_hotlist: true  },
    executive: { is_featured: true,  is_hotlist: true  },
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.title.trim() || !form.organization.trim() || !form.contact_email.trim()) {
      toast.error('Please fill in all required fields.');
      return;
    }
    if (!form.apply_url && !form.apply_email) {
      toast.error('Please provide an apply URL or email address.');
      return;
    }
    setSubmitting(true);
    try {
      const flags = planFlags[selectedPlan] || {};
      const payload = {
        title: form.title,
        organization: form.organization,
        logo_initial: form.logo_initial || form.organization?.slice(0,4).toUpperCase() || null,
        location: form.location,
        listing_type: form.listing_type,
        work_type: form.work_type,
        experience_level: form.experience_level,
        category: form.category,
        salary_range: form.salary_range || null,
        description: form.description,
        description_short: form.description_short,
        skills: form.skills.split(',').map(s => s.trim()).filter(Boolean),
        requirements: form.requirements.split('\n').map(s => s.trim()).filter(Boolean),
        apply_method: form.apply_method,
        apply_url: form.apply_url || null,
        apply_email: form.apply_email || null,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        plan_tier: selectedPlan,
        price_cents: finalPrice * 100,
        duration_days: selectedPlanData?.duration_days || 45,
        final_price_cents: finalPrice * 100,
        payment_method_selected: form.payment_method,
        payment_status: finalPrice === 0 ? 'complimentary' : 'manual_pending',
        coupon_code: couponStatus === 'applied' ? coupon : null,
        coupon_status: couponStatus,
        discount_percent: couponStatus === 'applied' ? couponDiscount : null,
        company_logo_url: form.company_logo_url || null,
        company_banner_url: form.company_banner_url || null,
        post_count: selectedPlanData?.post_count || 1,
        ...flags,
      };
      const res = await axios.post(`${API}/jobs`, payload);
      setSubmittedId(res.data.id);
      setSubmitted(true);
      toast.success('Listing submitted for review.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success screen ──────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="ch-post-success" data-testid="post-job-success">
        <div className="ch-post-success__inner">
          <div className="ch-post-success__icon"><Check size={32} color="#fff" /></div>
          <h1 data-testid="post-job-success-heading">Listing submitted.</h1>
          <p>Your listing is under review. Our team typically reviews within 1 business day.</p>
          {submittedId && (
            <p style={{ fontSize: 12, color: 'var(--op-muted)', marginTop: 4 }}>
              Reference ID: {submittedId}
            </p>
          )}
          <div className="ch-post-success__actions">
            <Link to="/career-hub" className="ch-post-success__btn ch-post-success__btn--primary" data-testid="post-success-view-hub">
              View Career Hub
            </Link>
            <button
              type="button"
              className="ch-post-success__btn ch-post-success__btn--ghost"
              onClick={() => { setSubmitted(false); setSubmittedId(null); setForm({ organization: '', logo_initial: '', contact_name: '', contact_email: '', contact_phone: '', title: '', location: '', listing_type: 'job', work_type: 'hybrid', experience_level: 'mid', category: 'technology', salary_range: '', description: '', description_short: '', skills: '', requirements: '', apply_method: 'external_url', apply_url: '', apply_email: '', company_logo_url: '', company_banner_url: '', payment_method: 'credit_card' }); }}
              data-testid="post-success-post-another"
            >
              Post Another Listing
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main form render ────────────────────────────────────────────────────────
  return (
    <div className="ch-post-wrap" data-testid="post-job-page">

      {/* ── Breadcrumb ── */}
      <div className="ch-post-breadcrumb">
        <div className="ch-post-breadcrumb__inner">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Link to="/career-hub" className="ch-breadcrumb-link">
              <ChevronLeft size={14} /> Career Hub
            </Link>
            <ChevronRight size={12} style={{ color: 'var(--op-muted)', opacity: 0.5 }} />
            <span style={{ color: 'var(--op-ink)', fontWeight: 600 }}>Post a Listing</span>
          </div>
          <span className="ch-phase-badge">Phase 1A · Test Mode · No real payments</span>
        </div>
      </div>

      <form onSubmit={handleSubmit}>

        {/* ══════════════════════════════════════════════════════════════════════
            FORM DETAILS + SIDEBAR — 2-column grid
        ══════════════════════════════════════════════════════════════════════ */}
        <div className="ch-post-form-grid">

          {/* Left — Form panels */}
          <div className="ch-post-form-col">

            {/* ── Step 1: Employer/Organization Info ── */}
            <div className="ch-form-card" data-testid="step-employer">
              <div className="ch-form-card__head">
                <span className="ch-step-chip">1</span>
                <h3>Employer / Organization Information</h3>
              </div>
              <div className="ch-form-grid-2">
                <div className="ch-field">
                  <label className="ch-label">Organization Name <span className="ch-req">*</span></label>
                  <input className="ch-input" value={form.organization} onChange={f('organization')} required placeholder="e.g. Teach For America" data-testid="field-organization" />
                </div>
                <div className="ch-field">
                  <label className="ch-label">Logo Initials <span className="ch-hint-inline">(2-4 chars)</span></label>
                  <input className="ch-input" value={form.logo_initial || ''} onChange={e => setForm(prev => ({ ...prev, logo_initial: e.target.value.toUpperCase().slice(0,4) }))} placeholder="e.g. TFA" maxLength={4} data-testid="field-logo-initial" />
                </div>
                <div className="ch-field">
                  <label className="ch-label">Hiring Contact Name <span className="ch-req">*</span></label>
                  <input className="ch-input" value={form.contact_name} onChange={f('contact_name')} placeholder="Full name" data-testid="field-contact-name" />
                </div>
                <div className="ch-field">
                  <label className="ch-label">Contact Email <span className="ch-req">*</span></label>
                  <input className="ch-input" type="email" value={form.contact_email} onChange={f('contact_email')} required placeholder="hr@organization.org" data-testid="field-contact-email" />
                </div>
                <div className="ch-field ch-field--full">
                  <label className="ch-label">Contact Phone <span className="ch-hint-inline">(optional)</span></label>
                  <input className="ch-input" type="tel" value={form.contact_phone} onChange={f('contact_phone')} placeholder="+1 (555) 000-0000" data-testid="field-contact-phone" style={{ maxWidth: 260 }} />
                </div>
              </div>
            </div>

            {/* ── Step 2: Listing Details ── */}
            <div className="ch-form-card" data-testid="step-listing">
              <div className="ch-form-card__head">
                <span className="ch-step-chip">2</span>
                <h3>Listing Details</h3>
              </div>
              <div className="ch-form-grid-2">
                <div className="ch-field ch-field--full">
                  <label className="ch-label">Job / Listing Title <span className="ch-req">*</span></label>
                  <input className="ch-input" value={form.title} onChange={f('title')} required placeholder="e.g. Policy Analyst — Economic Mobility" data-testid="field-title" />
                </div>
                <div className="ch-field">
                  <label className="ch-label">Listing Type <span className="ch-req">*</span></label>
                  <select className="ch-input" value={form.listing_type} onChange={f('listing_type')} data-testid="field-listing-type">
                    <option value="job">Job</option>
                    <option value="internship">Internship</option>
                    <option value="fellowship">Fellowship</option>
                    <option value="contract">Contract</option>
                    <option value="apprenticeship">Apprenticeship</option>
                  </select>
                </div>
                <div className="ch-field">
                  <label className="ch-label">Work Type <span className="ch-req">*</span></label>
                  <select className="ch-input" value={form.work_type} onChange={f('work_type')} data-testid="field-work-type">
                    <option value="on_site">On-Site</option>
                    <option value="remote">Remote</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>
                <div className="ch-field">
                  <label className="ch-label">Category / Pillar <span className="ch-req">*</span></label>
                  <select className="ch-input" value={form.category} onChange={f('category')} data-testid="field-category">
                    <option value="technology">Technology</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="education">Education</option>
                    <option value="government">Government</option>
                    <option value="business">Business</option>
                    <option value="nonprofit">Nonprofit</option>
                    <option value="media">Media & Communications</option>
                    <option value="finance">Finance</option>
                  </select>
                </div>
                <div className="ch-field">
                  <label className="ch-label">Location <span className="ch-req">*</span></label>
                  <input className="ch-input" value={form.location} onChange={f('location')} placeholder="City, State or Remote (US)" data-testid="field-location" />
                </div>
                <div className="ch-field">
                  <label className="ch-label">Experience Level</label>
                  <select className="ch-input" value={form.experience_level} onChange={f('experience_level')} data-testid="field-experience">
                    <option value="entry">Entry Level</option>
                    <option value="mid">Mid Career (3-7 yrs)</option>
                    <option value="senior">Senior (7+ yrs)</option>
                    <option value="executive">Executive</option>
                  </select>
                </div>
                <div className="ch-field ch-field--full">
                  <label className="ch-label">Salary / Compensation Range <span className="ch-hint-inline">(optional)</span></label>
                  <input className="ch-input" value={form.salary_range} onChange={f('salary_range')} placeholder="e.g. $65,000–$85,000 or Competitive + benefits" data-testid="field-salary" />
                </div>
                <div className="ch-field ch-field--full">
                  <label className="ch-label">Short Description <span className="ch-hint-inline">(1–2 sentences for listing card)</span> <span className="ch-req">*</span></label>
                  <input className="ch-input" value={form.description_short} onChange={f('description_short')} placeholder="A brief summary that appears on the listing card..." data-testid="field-desc-short" />
                </div>
                <div className="ch-field ch-field--full">
                  <label className="ch-label">Full Description</label>
                  <textarea className="ch-input ch-textarea" rows={7} value={form.description} onChange={f('description')} required placeholder="Full role description, responsibilities, org mission..." data-testid="field-description" />
                </div>
                <div className="ch-field ch-field--full">
                  <label className="ch-label">Skills / Tags <span className="ch-hint-inline">(press Enter to add, max 8)</span></label>
                  <input className="ch-input" value={form.skills} onChange={f('skills')} placeholder="e.g. Policy Analysis" data-testid="field-skills" />
                </div>
              </div>

              {/* Executive extras */}
              {selectedPlan === 'executive' && (
                <div className="ch-executive-extras" data-testid="executive-extras">
                  <div className="ch-executive-extras__head">
                    <Zap size={14} style={{ color: 'var(--op-navy)' }} />
                    <span>Executive Plan — Branding Assets</span>
                  </div>
                  <div className="ch-form-grid-2">
                    <div className="ch-field">
                      <label className="ch-label">Company Logo URL</label>
                      <input className="ch-input" type="url" value={form.company_logo_url} onChange={f('company_logo_url')} placeholder="https://acme.com/logo.png" data-testid="field-logo-url" />
                      <span className="ch-hint">Shown on all 3 of your listings</span>
                    </div>
                    <div className="ch-field">
                      <label className="ch-label">Company Banner URL</label>
                      <input className="ch-input" type="url" value={form.company_banner_url} onChange={f('company_banner_url')} placeholder="https://acme.com/banner.jpg" data-testid="field-banner-url" />
                      <span className="ch-hint">1200 × 400px recommended</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ── Step 3: How to Apply ── */}
            <div className="ch-form-card" data-testid="step-apply">
              <div className="ch-form-card__head">
                <span className="ch-step-chip">3</span>
                <h3>How Should Candidates Apply?</h3>
              </div>
              <div className="ch-form-grid-2">
                <div className="ch-field ch-field--full">
                  <div className="ch-apply-tabs" data-testid="apply-method-tabs">
                    {[
                      { val: 'external_url', label: 'External URL' },
                      { val: 'email', label: 'Email Address' },
                      { val: 'both', label: 'Both URL & Email' },
                    ].map(opt => (
                      <button
                        key={opt.val}
                        type="button"
                        className={`ch-apply-tab${form.apply_method === opt.val || (opt.val === 'external_url' && form.apply_method === 'both') ? ' ch-apply-tab--active' : ''}`}
                        onClick={() => setForm(prev => ({ ...prev, apply_method: opt.val }))}
                        data-testid={`apply-tab-${opt.val}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                {(form.apply_method === 'external_url' || form.apply_method === 'both') && (
                  <div className="ch-field ch-field--full">
                    <label className="ch-label">Application URL <span className="ch-req">*</span></label>
                    <input className="ch-input" type="url" value={form.apply_url} onChange={f('apply_url')} placeholder="https://yourcompany.com/careers/apply" data-testid="field-apply-url" />
                  </div>
                )}
                {(form.apply_method === 'email' || form.apply_method === 'both') && (
                  <div className="ch-field ch-field--full">
                    <label className="ch-label">Application Email <span className="ch-req">*</span></label>
                    <input className="ch-input" type="email" value={form.apply_email} onChange={f('apply_email')} placeholder="apply@yourorganization.org" data-testid="field-apply-email" />
                  </div>
                )}
              </div>
            </div>

            {/* ── Step 4: Select a Listing Plan ── */}
            <div className="ch-form-card" data-testid="step-plan">
              <div className="ch-form-card__head">
                <span className="ch-step-chip">4</span>
                <h3>Select a Listing Plan</h3>
              </div>

              {plansLoading ? (
                <div className="ch-plan-skeleton" data-testid="plan-skeleton">
                  {[0,1,2].map(i => <div key={i} className="ch-plan-skeleton__card" />)}
                </div>
              ) : (
                <div className="ch-plan-grid" data-testid="plan-grid">
                  {plans.filter(p => !p.is_gated || promoUnlocksFree).map(plan => (
                    <PlanCard
                      key={plan.key}
                      plan={plan}
                      selected={selectedPlan === plan.key}
                      onSelect={setSelectedPlan}
                      promoUnlocksFree={promoUnlocksFree}
                    />
                  ))}
                </div>
              )}

              <div style={{ marginTop: 24 }}>
                <label className="ch-label" style={{ marginBottom: 8, display: 'block' }}>Coupon / Comp Code <span className="ch-hint-inline">(optional)</span></label>
                <CouponRow
                  coupon={coupon}
                  onChange={setCoupon}
                  onApply={applyCoupon}
                  onRemove={removeCoupon}
                  status={couponStatus}
                  discount={couponDiscount}
                />
              </div>

              <p className="ch-plan-footer-note" data-testid="plan-edu-note">
                Learning institutions and nonprofits may qualify for additional discounts.{' '}
                <Link to="/contact" style={{ color: 'var(--op-teal)', fontWeight: 600 }}>
                  Contact Support →
                </Link>
              </p>
            </div>

          </div>{/* /form col */}

          {/* Right — Sticky sidebar */}
          <aside className="ch-post-sidebar" data-testid="order-summary-sidebar">
            <div className="ch-sidebar-card">
              <h3 className="ch-sidebar-title">Order Summary</h3>

              {selectedPlanData ? (
                <>
                  <div className="ch-summary-row ch-summary-row--plan">
                    <span className="ch-summary-plan-name" data-testid="summary-plan-name">
                      {selectedPlanData.label} Plan
                    </span>
                    <span className="ch-summary-plan-price" data-testid="summary-base-price">
                      {selectedPlanData.price_label}
                    </span>
                  </div>
                  <div className="ch-summary-meta">
                    <span>{selectedPlanData.post_count} listing{selectedPlanData.post_count > 1 ? 's' : ''}</span>
                    <span>{selectedPlanData.duration_days}-day duration</span>
                  </div>
                  {couponStatus === 'applied' && (
                    <div className="ch-summary-row ch-summary-row--discount" data-testid="summary-discount">
                      <span>Discount ({couponDiscount}%)</span>
                      <span>−${discountAmt}</span>
                    </div>
                  )}
                  <div className="ch-summary-divider" />
                  <div className="ch-summary-row ch-summary-row--total">
                    <span>Total</span>
                    <span data-testid="summary-total">{finalPrice === 0 ? 'Free' : `$${finalPrice}`}</span>
                  </div>
                </>
              ) : (
                <p className="ch-hint">No plan selected.</p>
              )}

              <button
                type="submit"
                disabled={submitting || !selectedPlanData}
                className="ch-submit-btn"
                data-testid="submit-listing-btn"
              >
                {submitting ? 'Submitting…' : 'Submit Listing for Review →'}
              </button>

              <p className="ch-hint" style={{ textAlign: 'center', marginTop: 8 }}>
                Listings are reviewed by Opportunity Post within 1-2 business days.
              </p>
            </div>
          </aside>

        </div>{/* /form grid */}

      </form>

      <style>{`
        /* ── Page shell ── */
        .ch-post-wrap { background: var(--op-paper); min-height: 100vh; }

        /* ── Breadcrumb ── */
        .ch-post-breadcrumb {
          position: sticky; top: 0; z-index: 50;
          background: var(--op-paper); border-bottom: 1px solid var(--op-border);
          padding: 0;
        }
        .ch-post-breadcrumb__inner {
          max-width: 1400px; margin: 0 auto;
          padding: 11px 32px;
          display: flex; align-items: center; justify-content: space-between; gap: 8px;
          font-size: 13px; color: var(--op-muted);
        }
        .ch-breadcrumb-link {
          display: inline-flex; align-items: center; gap: 4px;
          color: var(--op-muted); text-decoration: none; font-weight: 500;
        }
        .ch-breadcrumb-link:hover { color: var(--op-navy); }
        .ch-phase-badge {
          font-size: 11px; font-weight: 600; color: var(--op-muted);
          background: rgba(102,112,109,.08); border: 1px solid var(--op-border);
          border-radius: 20px; padding: 3px 10px; white-space: nowrap;
        }

        /* ── Step chip ── */
        .ch-step-chip {
          display: inline-flex; align-items: center; justify-content: center;
          width: 24px; height: 24px;
          background: var(--op-teal); color: #fff;
          border-radius: 50%;
          font-size: 11px; font-weight: 800; flex-shrink: 0;
        }

        /* ── Plan grid — 3-col ── */
        .ch-plan-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 14px;
        }
        @media (max-width: 900px) {
          .ch-plan-grid { grid-template-columns: 1fr 1fr; }
        }
        @media (max-width: 600px) {
          .ch-plan-grid { grid-template-columns: 1fr; }
        }

        /* ── Plan skeleton ── */
        .ch-plan-skeleton { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
        .ch-plan-skeleton__card {
          height: 280px; border-radius: 12px;
          background: var(--op-cream); border: 1px solid var(--op-border);
          animation: pulse 1.5s infinite;
        }
        @keyframes pulse { 0%,100%{opacity:.5} 50%{opacity:1} }

        /* ── Plan Card ── */
        .plan-card {
          background: var(--plan-bg, var(--op-cream));
          border: 2px solid transparent;
          border-radius: 12px;
          padding: 20px 20px 18px;
          cursor: pointer;
          transition: border-color .18s, box-shadow .18s, transform .14s;
          min-height: 320px;
          display: flex; flex-direction: column; gap: 12px;
          position: relative;
        }
        .plan-card:hover:not(.plan-card--locked) {
          border-color: var(--plan-accent, var(--op-teal));
          box-shadow: 0 6px 24px rgba(0,0,0,.10);
          transform: translateY(-2px);
        }
        .plan-card--selected {
          border-color: var(--plan-accent, var(--op-teal)) !important;
          box-shadow: 0 0 0 3px rgba(0,140,134,.10), 0 6px 24px rgba(0,0,0,.10);
        }
        .plan-card--locked {
          opacity: .72; cursor: not-allowed;
          filter: grayscale(20%);
        }

        /* Head */
        .plan-card__head { display: flex; align-items: flex-start; justify-content: space-between; }
        .plan-card__icon-wrap {
          width: 36px; height: 36px; border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          background: var(--plan-accent, var(--op-teal)); color: #fff;
          flex-shrink: 0;
        }
        .plan-card__badges { display: flex; flex-wrap: wrap; gap: 5px; justify-content: flex-end; }

        /* Badge variants */
        .plan-badge {
          display: inline-flex; align-items: center;
          padding: 2px 8px; border-radius: 20px;
          font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em;
        }
        .plan-badge-popular  { background: rgba(0,140,134,.14); color: var(--op-teal); border: 1px solid rgba(0,140,134,.28); }
        .plan-badge-teal     { background: rgba(0,140,134,.12); color: var(--op-teal); border: 1px solid rgba(0,140,134,.2); }
        .plan-badge-gold     { background: rgba(217,162,69,.14); color: #b37a1a; border: 1px solid rgba(217,162,69,.28); }
        .plan-badge-navy     { background: rgba(8,29,48,.1); color: var(--op-navy); border: 1px solid rgba(8,29,48,.2); }
        .plan-badge-muted    { background: rgba(102,112,109,.1); color: var(--op-muted); border: 1px solid rgba(102,112,109,.2); }
        .plan-badge-locked   { background: rgba(102,112,109,.1); color: var(--op-muted); border: 1px solid rgba(102,112,109,.15); }

        /* Price */
        .plan-card__price { display: flex; align-items: baseline; gap: 5px; }
        .plan-card__price-main {
          font-size: 28px; font-weight: 900; color: var(--plan-accent, var(--op-ink));
          font-family: 'Inter Tight', sans-serif; letter-spacing: -0.03em; line-height: 1;
        }
        .plan-card__price-sub { font-size: 11px; color: var(--op-muted); }

        /* Label */
        .plan-card__label {
          font-size: 17px; font-weight: 800; color: var(--op-ink);
          font-family: 'Inter Tight', sans-serif; margin: 0; letter-spacing: -0.02em;
        }

        /* Stats */
        .plan-card__stats {
          display: flex; align-items: center; gap: 12px;
          padding: 10px 0; border-top: 1px solid var(--op-border); border-bottom: 1px solid var(--op-border);
        }
        .plan-stat { display: flex; flex-direction: column; align-items: center; flex: 1; }
        .plan-stat__num { font-size: 18px; font-weight: 800; color: var(--op-ink); line-height: 1; }
        .plan-stat__lbl { font-size: 10px; color: var(--op-muted); text-transform: uppercase; letter-spacing: .06em; margin-top: 2px; }
        .plan-stat__divider { width: 1px; height: 28px; background: var(--op-border); }

        /* Perks */
        .plan-card__perks { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 7px; flex: 1; }
        .plan-card__perk { display: flex; align-items: flex-start; gap: 7px; font-size: 12px; color: var(--op-ink); line-height: 1.45; }
        .plan-perk-check { color: var(--plan-accent, var(--op-teal)); flex-shrink: 0; margin-top: 1px; }

        /* Locked note */
        .plan-card__locked-note {
          display: flex; align-items: flex-start; gap: 4px;
          font-size: 11px; color: var(--op-muted); line-height: 1.4; margin: 0;
          background: rgba(102,112,109,.07); border-radius: 6px; padding: 7px 9px;
        }

        /* CTA button */
        .plan-card__cta {
          display: flex; align-items: center; justify-content: center; gap: 5px;
          padding: 9px 0; border-radius: 8px; margin-top: auto;
          font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em;
          color: var(--plan-accent, var(--op-teal));
          border: 1.5px solid var(--plan-accent, var(--op-teal));
          transition: background .15s, color .15s;
        }
        .plan-card:hover:not(.plan-card--locked) .plan-card__cta {
          background: var(--plan-accent, var(--op-teal)); color: #fff;
        }
        .plan-card__cta--selected {
          background: var(--plan-accent, var(--op-teal)); color: #fff;
        }

        /* Footer note */
        .ch-plan-footer-note {
          margin-top: 16px; font-size: 13px; color: var(--op-muted);
        }

        /* ── Apply tabs ── */
        .ch-apply-tabs { display: flex; gap: 8px; }
        .ch-apply-tab {
          padding: 8px 18px; border-radius: 8px; border: 1.5px solid var(--op-border);
          background: var(--op-paper); color: var(--op-muted);
          font-size: 13px; font-weight: 600; cursor: pointer;
          transition: border-color .14s, background .14s, color .14s;
        }
        .ch-apply-tab--active {
          border-color: var(--op-teal); background: var(--op-teal-soft);
          color: var(--op-teal);
        }

        /* ── Form grid ── */
        .ch-post-form-grid {
          max-width: 1400px; margin: 0 auto;
          padding: 36px 32px 80px;
          display: grid; grid-template-columns: 1fr 300px; gap: 28px; align-items: start;
        }
        @media (max-width: 960px) {
          .ch-post-form-grid { grid-template-columns: 1fr; padding: 24px 16px 60px; }
        }

        /* ── Form cards ── */
        .ch-form-card {
          background: var(--op-paper-2); border: 1px solid var(--op-border);
          border-radius: 14px; padding: 26px; margin-bottom: 18px;
        }
        .ch-form-card__head {
          display: flex; align-items: center; gap: 10px; margin-bottom: 22px;
          flex-wrap: wrap;
        }
        .ch-form-card__head h3 {
          font-size: 16px; font-weight: 700; color: var(--op-ink);
          font-family: 'Inter Tight', sans-serif; margin: 0;
        }
        .ch-test-mode-badge {
          margin-left: auto; padding: 3px 10px; border-radius: 20px;
          background: rgba(217,162,69,.12); color: #b37a1a; border: 1px solid rgba(217,162,69,.25);
          font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em;
        }

        /* Form fields */
        .ch-form-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        @media (max-width: 600px) { .ch-form-grid-2 { grid-template-columns: 1fr; } }
        .ch-field { display: flex; flex-direction: column; gap: 6px; }
        .ch-field--full { grid-column: 1 / -1; }
        .ch-label { font-size: 12px; font-weight: 600; color: var(--op-ink); text-transform: uppercase; letter-spacing: .06em; }
        .ch-req { color: var(--op-teal); }
        .ch-hint { font-size: 11px; color: var(--op-muted); }
        .ch-hint-inline { font-size: 11px; color: var(--op-muted); font-weight: 400; text-transform: none; letter-spacing: 0; }
        .ch-input {
          padding: 10px 14px; border: 1px solid var(--op-border);
          border-radius: 9px; background: var(--op-paper); color: var(--op-ink);
          font-size: 14px; outline: none; transition: border-color .15s;
          font-family: inherit; width: 100%; box-sizing: border-box;
        }
        .ch-input:focus { border-color: var(--op-teal); }
        .ch-textarea { resize: vertical; min-height: 100px; }

        /* Executive extras */
        .ch-executive-extras {
          margin-top: 20px; padding: 16px; border-radius: 10px;
          background: rgba(8,29,48,.04); border: 1px solid rgba(8,29,48,.12);
        }
        .ch-executive-extras__head {
          display: flex; align-items: center; gap: 7px; margin-bottom: 14px;
          font-size: 12px; font-weight: 700; color: var(--op-navy);
          text-transform: uppercase; letter-spacing: .06em;
        }

        /* Coupon */
        .ch-coupon-row { }
        .ch-coupon-input-wrap {
          display: flex; align-items: center; gap: 0;
          border: 1px solid var(--op-border); border-radius: 9px;
          background: var(--op-paper); overflow: hidden;
          position: relative;
        }
        .ch-coupon-icon { position: absolute; left: 14px; color: var(--op-muted); flex-shrink: 0; }
        .ch-coupon-input {
          flex: 1; padding: 10px 14px 10px 38px; border: none; background: transparent;
          font-size: 14px; color: var(--op-ink); outline: none; font-family: inherit; min-width: 0;
        }
        .ch-coupon-apply-btn {
          padding: 8px 16px; background: var(--op-teal); color: #fff;
          border: none; cursor: pointer; font-size: 13px; font-weight: 700;
          text-transform: uppercase; letter-spacing: .06em;
        }
        .ch-coupon-remove-btn {
          padding: 8px 12px; background: none; border: none; cursor: pointer;
          color: var(--op-muted);
        }
        .ch-coupon-msg {
          display: flex; align-items: center; gap: 5px;
          font-size: 12px; margin-top: 6px;
        }
        .ch-coupon-msg--ok  { color: var(--op-teal); }
        .ch-coupon-msg--err { color: #c0392b; }

        /* ── Sidebar ── */
        .ch-post-sidebar { position: sticky; top: 64px; }
        .ch-sidebar-card {
          background: var(--op-cream); border: 1px solid var(--op-border);
          border-radius: 14px; padding: 22px;
        }
        .ch-sidebar-title { font-size: 14px; font-weight: 800; color: var(--op-ink); margin: 0 0 16px; font-family: 'Inter Tight', sans-serif; }
        .ch-summary-row { display: flex; justify-content: space-between; align-items: center; font-size: 14px; }
        .ch-summary-row--plan { font-weight: 600; color: var(--op-ink); }
        .ch-summary-row--discount { color: var(--op-teal); font-size: 13px; }
        .ch-summary-row--total { font-weight: 800; font-size: 16px; color: var(--op-ink); }
        .ch-summary-plan-name { color: var(--op-ink); }
        .ch-summary-plan-price { color: var(--op-ink); }
        .ch-summary-meta {
          display: flex; gap: 12px; font-size: 12px; color: var(--op-muted);
          margin: 4px 0 12px;
        }
        .ch-summary-divider { height: 1px; background: var(--op-border); margin: 12px 0; }
        .ch-submit-btn {
          width: 100%; padding: 13px; border-radius: 10px; border: none;
          background: var(--op-teal); color: #fff; font-size: 14px; font-weight: 800;
          cursor: pointer; margin-top: 14px;
          transition: opacity .15s;
        }
        .ch-submit-btn:disabled { opacity: .55; cursor: not-allowed; }
        .ch-submit-btn:hover:not(:disabled) { opacity: .88; }

        /* ── Success screen ── */
        .ch-post-success {
          min-height: 100vh; background: var(--op-paper);
          display: flex; align-items: center; justify-content: center; padding: 60px 24px;
        }
        .ch-post-success__inner { text-align: center; max-width: 500px; }
        .ch-post-success__icon {
          width: 64px; height: 64px; border-radius: 50%;
          background: var(--op-teal); display: flex; align-items: center; justify-content: center;
          margin: 0 auto 24px;
        }
        .ch-post-success__inner h1 {
          font-size: 28px; font-weight: 900; color: var(--op-ink); margin: 0 0 12px;
          font-family: 'Inter Tight', sans-serif;
        }
        .ch-post-success__inner p { font-size: 15px; color: var(--op-muted); margin: 0 0 24px; }
        .ch-post-success__actions { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }
        .ch-post-success__btn {
          padding: 12px 24px; border-radius: 10px; font-size: 14px; font-weight: 700;
          text-decoration: none; text-transform: uppercase; letter-spacing: .06em; cursor: pointer;
          display: inline-flex; align-items: center; border: none; transition: opacity .15s;
        }
        .ch-post-success__btn--primary { background: var(--op-navy); color: #fff; }
        .ch-post-success__btn--ghost { background: var(--op-cream); color: var(--op-ink); border: 1px solid var(--op-border); }

        @media (max-width: 768px) {
          .ch-post-breadcrumb__inner { padding: 10px 16px; }
        }
      `}</style>

    </div>
  );
}
